import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Paper,
  useMediaQuery,
  CircularProgress,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { useRefresh } from '../contexts/RefreshContext';
import useAppVersionCheck from '../hooks/useAppVersionCheck';
import { AnimatePresence, motion } from 'framer-motion';
import html2canvas from 'html2canvas';

// Importações de componentes
import EmployeeList from '../components/Employees/EmployeeList';
import SchedulePage from '../components/SchedulePage';
import SalesPage from '../components/SalesPage';
import PendingSalesPage from '../components/PendingSalesPage';
import ReportsPage from '../components/ReportsPage';
import ServicesPage from '../components/ServicesPage';
import PayoutsPage from '../components/PayoutsPage';
import ChatPage from '../components/ChatPage';
import EmployeeForm from '../components/EmployeeForm';
import ResortsPage from '../components/ResortsPage';
import InvoicePage from '../components/InvoicePage';
import ClocksPage from '../components/ClocksPage';
import MetasPage from '../components/MetasPage';
import DailySummaryView from '../components/DailySummaryView';
import OpeningChecklistView from '../components/OpeningCheckListView';
import ClosingChecklistView from '../components/ClosingChecklistView';
import DailyActivityReportView from '../components/DailyActivityReportView';
import MaterialPage from '../components/MaterialPage';
import TutorialPage from '../components/TutorialPage';
import PedidosPage from '../components/PedidosPage';
import GroupView from '../components/GroupView';
import DeletedSales from '../components/DeletedSales';
import SheetsViewer from '../components/SheetsViewer';
import SalaryHistory from '../components/SalaryHistoryPage';
import EmployeeSalaryHistory from '../components/EmployeeSalaryHistory';
import CashCountPage from '../components/CashCountPage';
import TabletManagementPage from '../components/TabletManagementPage';
import UploadPage from '../components/UploadPage';
import CloudPage from '../components/FileManager';
import Database from '../components/Database';
import Settings from '../components/Settings';
import EditPage from '../components/EditPage';
import ClockInOutPage from '../components/ClockInOutPage';
import WarningPage from '../components/WarningPage';
import SquarePaymentsPage from '../components/SquarePaymentsPage';
import ClosingChecklistsReport from '../components/ClosingChecklistsReport';
import api from '../services/api';

// Hooks customizados
import { useDashboardData } from '../hooks/useDashboardData';
import { useAdminDashboard } from '../hooks/useAdminDashboard';
import { usePerformanceData } from '../hooks/usePerformanceData';

// Componentes de dashboard
import { EmployeeDashboard } from '../components/Dashboard/EmployeeDashboard';
import { AdminOverview } from '../components/Dashboard/AdminOverview';
import { PerformanceTab } from '../components/Dashboard/PerformanceTab';
import { NavigationMenu } from '../components/Dashboard/NavigationMenu';
import { DashboardActions } from '../components/Dashboard/DashboardActions';

import Badge from '@mui/material/Badge';
import { useAdminNotifications } from '../hooks/useAdminNotifications';
import { AdminNotificationsTab } from '../components/Dashboard/AdminNotificationsTab';

// Constantes
const drawerWidth = 200;

const TAB_BAR_WIDTH_BY_ROLE = {
  1: 1200,
  2: 1200,
  3: 1200,
  4: 1200,
  5: 1200,
};

const Dashboard = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { refreshKey, triggerRefresh } = useRefresh();
  const { updateAvailable, reloadForUpdate } = useAppVersionCheck();

  // Estados locais do Dashboard
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [renderOffscreenSummary, setRenderOffscreenSummary] = useState(false);

  // Pending Sales (finalização de venda) só aparece se o admin ligar em Settings.
  // Chave ausente ou erro na chamada = escondida.
  const [showPendingSales, setShowPendingSales] = useState(false);
  useEffect(() => {
    api.get('/api/admin-settings')
      .then((r) => setShowPendingSales(r.data?.pending_sales_page === true))
      .catch(() => {});
  }, []);

  const [dailySummaryReportData, setDailySummaryReportData] = useState(null);
  const [openingChecklistData,   setOpeningChecklistData]   = useState(null);
  const [closingChecklistData,   setClosingChecklistData]   = useState(null);
  const [reportData,             setReportData]             = useState(null);

  const summaryRef = useRef();
  const openingRef = useRef();
  const closingRef = useRef();
  const reportRef  = useRef();

  // Hooks de dados
  const { dashboardData, loadingDashboard } = useDashboardData(user, refreshKey);
  const { adminDashboardData, loadingAdminData } = useAdminDashboard(user, refreshKey);
  const { performanceOverview, loadingPerformanceOverview, fetchPerformanceOverview } =
    usePerformanceData(user, tabValue);

  // Admin Notifications
  // A carga inicial vive dentro do hook — ele é quem sabe quando o contexto
  // está pronto (a supervisora depende do group_id resolver antes).
  const {
    notifications,
    count: notificationsCount,
    loading: loadingNotifications,
    resolve: resolveNotif,
    refresh: refreshAdminNotifs, // usado pelo botão de refresh manual
  } = useAdminNotifications(user);

  // Handlers
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isEmployeeClockedIn = (() => {
    const clockStr = dashboardData?.clock || '--:-- | --:--';
    const [clockIn, clockOut] = clockStr.split(' | ');
    return clockIn !== '--:--' && clockOut === '--:--';
  })();

  const toggleDrawer = () => setMobileOpen(!mobileOpen);

    const handleDirectShareWithoutModal = async () => {
    if (isGeneratingImage) return;

    if (!dashboardData || !dashboardData.date) {
      alert("Dashboard data not available to fetch summary.");
      return;
    }

    // ✅ Reseta tudo antes de começar — garante que o useEffect dispara mesmo numa segunda chamada
    setDailySummaryReportData(null);
    setOpeningChecklistData(null);
    setClosingChecklistData(null);
    setReportData(null);
    setRenderOffscreenSummary(false);

    // Pequeno delay pra garantir que o React processou o reset antes de setar os novos dados
    await new Promise(r => setTimeout(r, 50));

    setIsGeneratingImage(true);
    setRenderOffscreenSummary(true);

    const parts = dashboardData.date.split('/');
    const apiDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;

    try {
      const [summaryRes, openingRes, closingRes, reportRes] = await Promise.allSettled([
        api.get(`/daily_summary_data?date=${apiDate}`),
        api.get('/my-opening-today'),
        api.get('/my-closing-today'),
        api.get('/my-report-today'),
      ]);

      // Daily summary é obrigatório
      if (summaryRes.status === 'fulfilled') {
        setDailySummaryReportData(summaryRes.value.data);
      } else {
        console.error('Error fetching daily summary:', summaryRes.reason);
        alert('Failed to fetch summary data.');
        setRenderOffscreenSummary(false);
        setIsGeneratingImage(false);
        return;
      }

      // Opening — opcional
      if (openingRes.status === 'fulfilled' && openingRes.value.data?.exists) {
        setOpeningChecklistData({
          ...openingRes.value.data,
          date:   dashboardData.date,
          name:   dashboardData.name   || user?.name   || '',
          resort: dashboardData.resort || '',
        });
      } else {
        setOpeningChecklistData(null);
      }

      // Closing — opcional
      if (closingRes.status === 'fulfilled' && closingRes.value.data?.exists) {
        setClosingChecklistData({
          ...closingRes.value.data,
          date:   dashboardData.date,
          name:   dashboardData.name   || user?.name   || '',
          resort: dashboardData.resort || '',
        });
      } else {
        setClosingChecklistData(null);
      }

      // Daily activity report — opcional
      if (reportRes.status === 'fulfilled' && reportRes.value.data?.exists) {
        setReportData({
          ...reportRes.value.data,
          date:   dashboardData.date,
          name:   dashboardData.name   || user?.name   || '',
          resort: dashboardData.resort || '',
        });
      } else {
        setReportData(null);
      }

    } catch (err) {
      console.error('Error fetching data for share:', err);
      alert('Failed to fetch data.');
      setDailySummaryReportData(null);
      setOpeningChecklistData(null);
      setClosingChecklistData(null);
      setReportData(null);
      setRenderOffscreenSummary(false);
      setIsGeneratingImage(false);
    }
  };

  const performImageGenerationAndShare = useCallback(async (action = 'share') => {
    if (!summaryRef.current || !dailySummaryReportData) {
      alert("Summary component or data not ready for sharing.");
      throw new Error("Summary component or data not ready");
    }

    let shareText = `Daily Log for ${dailySummaryReportData.name || 'Employee'} on ${dailySummaryReportData.date || 'N/A'}.\n`;
    shareText += `Resort: ${dailySummaryReportData.resort || 'N/A'}\n\n`;

    if (dailySummaryReportData.financials_hidden) {
      shareText += `Sales and tips are hidden while employee is clocked in.\n\n`;
    } else {
      shareText += `Grand Total: $${dailySummaryReportData.grand_total !== undefined
        ? parseFloat(dailySummaryReportData.grand_total).toFixed(2)
        : '0.00'}\n`;
      shareText += `Tip: $${dailySummaryReportData.total_tips !== undefined
        ? parseFloat(dailySummaryReportData.total_tips).toFixed(2)
        : '0.00'}\n\n`;
    }

    shareText += "Materials & Supplies:\n";
    if (dailySummaryReportData.material_val) shareText += `Material: ${dailySummaryReportData.material_val}\n`;
    shareText += `Pre-mades In: ${dailySummaryReportData.pre_mades_in_val || 0}\n`;
    shareText += `Pre-mades Out: ${dailySummaryReportData.pre_mades_out_val || 0}\n`;
    shareText += `Combs In: ${dailySummaryReportData.combs_in_val || 0}\n`;
    shareText += `Combs Out: ${dailySummaryReportData.combs_out_val || 0}\n`;
    shareText += `Metal Clips: ${dailySummaryReportData.metal_clips_val || 0}\n`;
    shareText += `Plastic Clips: ${dailySummaryReportData.plastic_clips_val || 0}\n`;
    shareText += `Scissors: ${dailySummaryReportData.scissors_val || 0}\n`;

    const employeeName = dailySummaryReportData.name?.replace(/\s+/g, '_') || 'employee';
    const reportDate   = dailySummaryReportData.date?.replace(/\//g, '-') || 'today';

    const canvasOpts = { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' };

    // Captura daily summary
    const summaryCanvas = await html2canvas(summaryRef.current, canvasOpts);
    const summaryImage  = summaryCanvas.toDataURL('image/png', 1.0);

    // Captura opening (se existir)
    let openingImage = null;
    if (openingRef.current && openingChecklistData) {
      const c = await html2canvas(openingRef.current, canvasOpts);
      openingImage = c.toDataURL('image/png', 1.0);
    }

    // Captura closing (se existir)
    let closingImage = null;
    if (closingRef.current && closingChecklistData) {
      const c = await html2canvas(closingRef.current, canvasOpts);
      closingImage = c.toDataURL('image/png', 1.0);
    }

    // Captura daily activity report (se existir)
    let reportImage = null;
    if (reportRef.current && reportData) {
      const c = await html2canvas(reportRef.current, canvasOpts);
      reportImage = c.toDataURL('image/png', 1.0);
    }

    const summaryFilename = `Daily_Summary_${employeeName}_${reportDate}.png`;
    const openingFilename = `Opening_${employeeName}_${reportDate}.png`;
    const closingFilename = `Closing_${employeeName}_${reportDate}.png`;
    const reportFilename  = `Activity_Report_${employeeName}_${reportDate}.png`;

    const toFile = async (dataUrl, name) => {
      const blob = await (await fetch(dataUrl)).blob();
      return new File([blob], name, { type: 'image/png' });
    };

    const dl = (href, name) => {
      const a = document.createElement('a');
      a.href = href; a.download = name;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    };

    if (action === 'download') {
      dl(summaryImage, summaryFilename);
      if (openingImage) dl(openingImage, openingFilename);
      if (closingImage) dl(closingImage, closingFilename);
      if (reportImage)  dl(reportImage,  reportFilename);

    } else if (action === 'open') {
      const w = window.open();
      w.document.write(`<img src="${summaryImage}" style="max-width:100%;"/>`);
      if (openingImage) w.document.write(`<br/><img src="${openingImage}" style="max-width:100%;"/>`);
      if (closingImage) w.document.write(`<br/><img src="${closingImage}" style="max-width:100%;"/>`);
      if (reportImage)  w.document.write(`<br/><img src="${reportImage}"  style="max-width:100%;"/>`);

    } else if (action === 'share' && navigator.share) {
      const files = [];
      files.push(await toFile(summaryImage, summaryFilename));
      if (openingImage) files.push(await toFile(openingImage, openingFilename));
      if (closingImage) files.push(await toFile(closingImage, closingFilename));
      if (reportImage)  files.push(await toFile(reportImage,  reportFilename));
      await navigator.share({ title: 'Daily Work Summary', text: shareText, files });

    } else {
      // Fallback sem Share API
      alert('Share API not supported. Downloading images.');
      try { await navigator.clipboard.writeText(shareText); } catch (_) {}
      dl(summaryImage, summaryFilename);
    }
  }, [dailySummaryReportData, openingChecklistData, closingChecklistData, reportData]);

  // Action handlers

  // Print/delete/copy/visibility do Schedule abrem o mesmo modal la dentro do
  // SchedulePage - e ele que sabe quais semanas existem.
  const openScheduleAction = (action) =>
    window.dispatchEvent(new CustomEvent('scheduleAction', { detail: { action } }));

  const handleOpenHotelOrderModal = () => window.dispatchEvent(new CustomEvent('openHotelOrderModal'));
  const handleExportInvoice = () => window.dispatchEvent(new CustomEvent('exportInvoiceToExcel'));


  // Effects
  useEffect(() => setTabValue(0), [user, refreshKey]);

  useEffect(() => {
    if (isGeneratingImage && renderOffscreenSummary && dailySummaryReportData) {
      const timerId = setTimeout(async () => {
        if (summaryRef.current) {
          try {
          await performImageGenerationAndShare('share');
            } catch (error) {
              console.error("Error during image generation/share:", error);
              alert(`Failed to generate or share summary.\n\nErro: ${error?.message || String(error)}`);
            } finally {
            setIsGeneratingImage(false);
            setRenderOffscreenSummary(false);
            setOpeningChecklistData(null);
            setClosingChecklistData(null);
            setReportData(null);
          }
        } else {
          alert("Could not prepare summary for sharing. Please try again.");
          setIsGeneratingImage(false);
          setRenderOffscreenSummary(false);
        }
      }, 100);

      return () => clearTimeout(timerId);
    }
  }, [dailySummaryReportData, renderOffscreenSummary, isGeneratingImage, performImageGenerationAndShare]);

  const renderDashboardContent = () => {
    if (loadingDashboard && !dashboardData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 120px)' }}>
          <CircularProgress />
        </Box>
      );
    }

    const NotificationsLabel = (
      <Badge
        color="error"
        badgeContent={notificationsCount > 99 ? "99+" : notificationsCount}
        invisible={!notificationsCount}
        sx={{ "& .MuiBadge-badge": { borderRadius: "999px", fontWeight: 900, minWidth: 18, height: 18 } }}
      >
        NOTIFICATIONS
      </Badge>
    );

    const NotificationsTab = {
      label: NotificationsLabel,
      component: (
        <AdminNotificationsTab
          items={notifications}
          loading={loadingNotifications}
          onResolve={resolveNotif}
          onOpenSquare={() => navigate("/square")}
          onOpenEditRequests={() => navigate("/editpage")}
          onOpenOffRequests={() => navigate("/schedule")}
        />
      ),
    };

    const getDashboardTabs = () => {
      switch (user?.role_id) {
        case 3:
          return [
            { label: "OVERVIEW",     component: <AdminOverview adminData={adminDashboardData} loading={loadingAdminData} /> },
            NotificationsTab,
            { label: "DASHBOARD",   component: <EmployeeDashboard dashboardData={dashboardData} user={user} /> },
            { label: "PERFORMANCE", component: <PerformanceTab performanceOverview={performanceOverview} loading={loadingPerformanceOverview} fetchData={fetchPerformanceOverview} /> },
          ];
        case 2:
          return [
            { label: "DASHBOARD",   component: <EmployeeDashboard dashboardData={dashboardData} user={user} /> },
            NotificationsTab,
            { label: "PERFORMANCE", component: <PerformanceTab performanceOverview={performanceOverview} loading={loadingPerformanceOverview} fetchData={fetchPerformanceOverview} /> },
          ];
        case 4:
          // Supervisora: mesma aba da gerência, mas o hook já entrega só as
          // notificações do grupo dela.
          return [
            { label: "DASHBOARD",   component: <EmployeeDashboard dashboardData={dashboardData} user={user} /> },
            NotificationsTab,
            { label: "PERFORMANCE", component: <PerformanceTab performanceOverview={performanceOverview} loading={loadingPerformanceOverview} fetchData={fetchPerformanceOverview} /> },
          ];
        case 1:
        default:
          return [
            { label: "DASHBOARD",   component: <EmployeeDashboard dashboardData={dashboardData} user={user} /> },
            { label: "PERFORMANCE", component: <PerformanceTab performanceOverview={performanceOverview} loading={loadingPerformanceOverview} fetchData={fetchPerformanceOverview} /> },
          ];
      }
    };

    const tabs = getDashboardTabs();
    const tabContainerWidth = TAB_BAR_WIDTH_BY_ROLE[user?.role_id] || 300;

    return (
      <Box sx={{ p: { xs: 1, md: 1 }, maxWidth: `${tabContainerWidth}px`, mx: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            p: 1, mb: 1, maxWidth: '1200px', mx: 'auto', borderRadius: 2,
            position: 'relative', overflow: 'hidden',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            backgroundImage: `linear-gradient(#fff, #fff), linear-gradient(180deg, #ec4899, #a855f7)`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            backgroundColor: '#fff',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons={false}
            allowScrollButtonsMobile={false}
            sx={{
              minHeight: 42, width: '100%',
              '& .MuiTabs-scroller': { overflowX: 'auto' },
              '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
              '@media (min-width: 900px)': {
                '& .MuiTabs-scroller': { overflowX: 'hidden', margin: '0 auto', width: 'fit-content' },
                '& .MuiTabs-flexContainer': { justifyContent: 'center' },
              },
              '& .MuiTabs-indicator': { height: 3, borderRadius: 2, background: 'linear-gradient(90deg, #ec4899, #a855f7)' },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                sx={{
                  textTransform: 'none', fontWeight: 500, minHeight: 42, minWidth: 120,
                  flexShrink: 0, px: 2, '& + .MuiTab-root': { ml: 1 },
                  color: '#6b7280',
                  '&.Mui-selected': { color: '#a855f7', fontWeight: 600 },
                  '&:hover': { color: '#ec4899', backgroundColor: 'rgba(236, 72, 153, 0.06)', borderRadius: 1 },
                }}
              />
            ))}
          </Tabs>
        </Paper>

        <Box sx={{ width: '100%', maxWidth: '1200px', mx: 'auto' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={tabValue}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              {tabs[tabValue]?.component}
            </motion.div>
          </AnimatePresence>
        </Box>
      </Box>
    );
  };

  const actionProps = {
    showRefreshButton:       location.pathname !== '/schedule',
    showPrintButton:         location.pathname === '/payouts' || location.pathname === '/schedule',
    showClearScheduleButton: location.pathname === '/schedule' && user?.role_id === 3,
    showOrderHotelsButton:   location.pathname === '/schedule' && user?.role_id === 3,
    showExportInvoiceButton: location.pathname === '/invoice'  && user?.role_id === 3,
    showShareButton:         user && (user.role_id >= 1) && location.pathname === '/',
    showCopyScheduleButton:  location.pathname === '/schedule' && (user?.role_id === 3 || user?.role_id === 2),
    showScheduleVisibilityButton: location.pathname === '/schedule' && (user?.role_id === 3 || user?.role_id === 2),
    onRefresh: async () => {
      triggerRefresh();
      await Promise.resolve(refreshAdminNotifs?.());
    },
    onPrint:         () => (location.pathname === '/schedule' ? openScheduleAction('print') : window.print()),
    onClearSchedule: () => openScheduleAction('delete'),
    onOrderHotels:   handleOpenHotelOrderModal,
    onExportInvoice: handleExportInvoice,
    onShare:         handleDirectShareWithoutModal,
    onCopySchedule:  () => openScheduleAction('copy'),
    onToggleScheduleVisibility: () => openScheduleAction('visibility'),
    onLogout:        handleLogout,
    isGeneratingImage,
    dashboardData,
    isEmployeeClockedIn,
    loadingDashboard,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: 'rgba(245, 245, 245, 0.8)' }}>
      <AppBar position="fixed" sx={{
        zIndex: 1300,
        background: 'linear-gradient(to right, #ffffff, #f5f5f5)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        borderBottom: '1px solid #e0e0e0',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease-in-out',
      }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {isMobile && (
            <IconButton edge="start" sx={{ color: 'rgb(127, 127, 127)' }} onClick={toggleDrawer}>
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <img src="/logo.png" alt="Logo" style={{ width: 40, height: 40, marginLeft: 8 }} />
          </Box>
          {updateAvailable && (
            <Button
              className="print-hide"
              size="small"
              variant="contained"
              color="warning"
              onClick={reloadForUpdate}
              sx={{ mr: 1, borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
            >
              New version — Update
            </Button>
          )}
          <DashboardActions {...actionProps} />
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flexGrow: 1, mt: { xs: 8, md: 10 }, minHeight: 'calc(100vh - 64px)' }}>
        <NavigationMenu
          user={user}
          location={location}
          navigate={navigate}
          isMobile={isMobile}
          mobileOpen={mobileOpen}
          toggleDrawer={toggleDrawer}
          setMobileOpen={setMobileOpen}
          showPendingSales={showPendingSales}
        />

        <Box component="main" sx={{
          position: 'absolute',
          top: 0,
          left: { xs: 0, md: `${drawerWidth}px` },
          right: 0,
          bottom: 0,
          overflowY: 'auto',
          // Toda rota entra deslizando de x:100 e sai para x:-100 (motion.div
          // abaixo). Durante a transição o conteúdo fica 100px fora da tela e
          // o celular ganha uma rolagem lateral — em qualquer página, não só na
          // de material. `clip` e não `hidden`: hidden viraria um segundo
          // container de rolagem e quebraria o position:sticky das páginas.
          overflowX: 'clip',
          backgroundColor: 'rgba(255, 255, 255, 0)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '65px',
        }}>
          <AnimatePresence mode="wait">
            {/* refreshKey entra na key da rota: o botão Refresh remonta a página
                atual, então TODA página recarrega seus dados — inclusive as que
                nunca chamaram useRefresh(). Remontar dispara o efeito de mount
                uma vez só, então quem já lia refreshKey não busca em dobro. */}
            <Routes location={location} key={`${location.pathname}#${refreshKey}`}>
              <Route path="/" element={
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                  {renderDashboardContent()}
                </motion.div>
              } />
              <Route path="/dashboard"             element={<Navigate to="/" />} />
              <Route path="/employees"             element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><EmployeeList /></motion.div>} />
              <Route path="/sales"                 element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><SalesPage /></motion.div>} />
              {/* desligada em Settings = rota vazia, como qualquer path que não existe aqui */}
              <Route path="/pending"               element={showPendingSales ? <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><PendingSalesPage /></motion.div> : null} />
              <Route path="/schedule"              element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><SchedulePage /></motion.div>} />
              <Route path="/reports"               element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><ReportsPage /></motion.div>} />
              <Route path="/services"              element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><ServicesPage /></motion.div>} />
              <Route path="/payouts"               element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><PayoutsPage /></motion.div>} />
              <Route path="/minibot"               element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><ChatPage /></motion.div>} />
              <Route path="/smartdaily"            element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><EmployeeForm /></motion.div>} />
              <Route path="/clocks"                element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><ClocksPage /></motion.div>} />
              <Route path="/invoice"               element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><InvoicePage /></motion.div>} />
              <Route path="/material"              element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><MaterialPage /></motion.div>} />
              <Route path="/resorts"               element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><ResortsPage /></motion.div>} />
              <Route path="/guide"                 element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><TutorialPage /></motion.div>} />
              <Route path="/requests"              element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><PedidosPage /></motion.div>} />
              <Route path="/metas"                 element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><MetasPage /></motion.div>} />
              <Route path="/groups"                element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><GroupView /></motion.div>} />
              <Route path="/delsales"              element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><DeletedSales /></motion.div>} />
              <Route path="/sheets"                element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><SheetsViewer /></motion.div>} />
              <Route path="/salaryhistory"         element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><SalaryHistory /></motion.div>} />
              <Route path="/employeesalaryhistory" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><EmployeeSalaryHistory /></motion.div>} />
              <Route path="/checkup"               element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><CashCountPage /></motion.div>} />
              <Route path="/devices"               element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><TabletManagementPage /></motion.div>} />
              <Route path="/upload"                element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><UploadPage /></motion.div>} />
              <Route path="/cloud"                 element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><CloudPage /></motion.div>} />
              <Route path="/database"              element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><Database /></motion.div>} />
              <Route path="/settings"              element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><Settings /></motion.div>} />
              <Route path="/editpage"              element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><EditPage /></motion.div>} />
              <Route path="/clockinout"            element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><ClockInOutPage /></motion.div>} />
              <Route path="/warningpage"           element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><WarningPage /></motion.div>} />
              <Route path="/square"                element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><SquarePaymentsPage /></motion.div>} />
              <Route path="/closingchecklists"     element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><ClosingChecklistsReport /></motion.div>} />
            </Routes>
          </AnimatePresence>
        </Box>
      </Box>

      {/* ── Offscreen renders para captura com html2canvas ── */}
      <Box sx={{ position: 'absolute', left: '-9999px', top: '-9999px', zIndex: -1000, backgroundColor: 'white' }}>
        {renderOffscreenSummary && dailySummaryReportData && (
          <DailySummaryView summaryData={dailySummaryReportData} ref={summaryRef} />
        )}
        {renderOffscreenSummary && openingChecklistData && (
          <OpeningChecklistView data={openingChecklistData} ref={openingRef} />
        )}
        {renderOffscreenSummary && closingChecklistData && (
          <ClosingChecklistView closingData={closingChecklistData} ref={closingRef} />
        )}
        {renderOffscreenSummary && reportData && (
          <DailyActivityReportView
            reportData={reportData}
            signatureSvg={closingChecklistData?.signature_svg}
            ref={reportRef}
          />
        )}
      </Box>

      <style jsx global>{`
        @media print {
          @page { size: letter landscape; margin: 6mm; }
          body * { visibility: hidden; }
          .printable-content, .printable-content * { visibility: visible; }
          .printable-content { position: static !important; width: 100% !important; max-width: 100% !important; }
          .MuiTableContainer-root, .MuiPaper-root, .MuiBox-root { overflow: visible !important; height: auto !important; max-height: none !important; }
          table { width: 100% !important; border-collapse: collapse; page-break-inside: auto; break-inside: auto; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          tr { page-break-inside: avoid; break-inside: avoid; page-break-after: auto; }
          th, td { border: 1px solid #000; padding: 3px 5px; font-size: 10px; white-space: normal; }
          .MuiAppBar-root, .MuiDrawer-root, nav, header, .no-print, .print-hide { display: none !important; }
        }
      `}</style>
    </Box>
  );
};

export default Dashboard;